// Library imports
import styled from 'styled-components';

export const GameCardWrapper = styled.div`
    background: #0f1117;
    /* Cap at the grid width so small phones (< ~410px) don't push the card off screen */
    min-width: min(375px, 100%);
    max-width: 550px;
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 12px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.3);
    overflow: hidden;
`;

export const GameCardHeaderWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 10px 14px;
    border-bottom: 1px solid rgba(255,255,255,0.07);
    background: linear-gradient(135deg, #0f1117 0%, #1a1f2e 100%);
    position: relative;
`;

export const GameCardTitleWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    min-width: 0;
    padding-right: 0.8rem;
`;

export const GameCardTitle = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: #f1f5f9;
    letter-spacing: -0.01em;
`;

export const GameCardSubtitle = styled.span`
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
`;

export const BadgeContainer = styled.div`
    position: absolute;
    top: 38px;
    left: 120px;
    display: flex;
    flex-direction: row;
    align-items: center;

    /* Small screens: back in flow so the compact Unlock button stacks below without overlap */
    @media (max-width: 600px) {
        position: static;
        margin-top: 4px;
        &:empty { display: none; }
    }
`;

export const HeaderButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-end;
    min-width: 250px;
    max-width: 62%;
    margin-left: auto;

    @media (max-width: 600px) {
        min-width: 0;
        max-width: none;
    }
`;

export const HeaderActionsRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
    flex-wrap: nowrap;
    width: 100%;

    @media (max-width: 600px) {
        gap: 0.35rem;
    }
`;

export const UnlockButton = styled.button`
    border: none;
    border-radius: 999px;
    padding: 0.38rem 0.95rem;
    background: linear-gradient(135deg, #facc15 0%, #f59e0b 50%, #d97706 100%);
    color: #111827;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
    box-shadow: 0 9px 18px rgba(245, 158, 11, 0.36);
    transition: transform 0.16s ease, box-shadow 0.16s ease, filter 0.16s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 12px 24px rgba(245, 158, 11, 0.45);
        filter: saturate(1.08);
    }

    &:active {
        transform: translateY(0);
    }

    /* No prediction posted yet: looks disabled but stays clickable to explain why. */
    ${({ $unavailable }) =>
        $unavailable &&
        `
        background: #4b5563;
        color: #d1d5db;
        box-shadow: none;
        cursor: not-allowed;
        &:hover { transform: none; box-shadow: none; filter: none; }
    `}

    /* Two copies render: the full button in the header's right corner, and a
       compact one under the title that takes over on small screens. */
    ${({ $compact }) =>
        $compact
            ? `
        display: none;
        @media (max-width: 600px) {
            display: inline-flex;
            align-items: center;
            margin-top: 6px;
            padding: 0.24rem 0.6rem;
            font-size: 0.64rem;
            letter-spacing: 0.03em;
            box-shadow: 0 4px 10px rgba(245, 158, 11, 0.3);
        }
    `
            : `
        @media (max-width: 600px) { display: none; }
    `}
`;

export const ContextBadgeContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.4rem;
    flex-wrap: nowrap;
`;

export const ContextBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.28rem;
    padding: 4px 10px;
    border-radius: 999px;
    border: 1px solid
        ${({ $kind }) => ($kind === 'division' ? 'rgba(96,165,250,0.52)' : 'rgba(45,212,191,0.52)')};
    background: ${({ $kind }) => ($kind === 'division' ? 'rgba(59,130,246,0.16)' : 'rgba(20,184,166,0.16)')};
    color: ${({ $kind }) => ($kind === 'division' ? '#bfdbfe' : '#99f6e4')};
    font-size: 0.67rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    white-space: nowrap;

    i {
        font-size: 0.58rem;
        opacity: 0.92;
    }
`;
export const GameCardContentWrapper = styled.div`
    box-sizing: border-box;
    position: relative;
    display: flex;
    height: 305px;
    flex-direction: row;
    justify-content: flex-start;
`;

export const Toolbar = styled.div`
    background: linear-gradient(135deg, #0f1117 0%, #1a1f2e 100%);
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
    border-top: 1px solid rgba(255,255,255,0.07);
`;

export const ToolbarButton = styled.button`
    flex: 1;
    background-color: ${({ active, disabled }) =>
        disabled ? 'rgba(255,255,255,0.03)' : active === 'true' ? '#b47806' : 'transparent'} !important;
    color: ${({ active, disabled }) =>
        disabled ? '#374151' : active === 'true' ? '#ffffff' : '#94a3b8'} !important;
    border: 0px;
    padding: 0.65rem 1rem;
    border-right: 1px solid rgba(255,255,255,0.06);
    border-bottom: none;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0.15rem;

    span {
        white-space: nowrap;
    }

    &:hover {
        cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
        background-color: ${({ disabled }) => (disabled ? 'rgba(255,255,255,0.03)' : '#f0c876')} !important;
        color: ${({ disabled }) => (disabled ? '#374151' : '#ffffff')} !important;
    }

    &:disabled {
        pointer-events: none;
        opacity: 0.6;
    }
`;
